<template>
    <div
        class="transition duration-500 ease-in"
        :class="{
            'opacity-0 h-0': !dataComputed?.points || dataComputed.points.length < 4,
            'opacity-100 h-auto pb-4': dataComputed?.points && dataComputed.points.length > 4,
        }"
    >
        <vx-chart-dot
            :selected="selectedDotsIndexes"
            @update:selected="handleSelection"
            :loading="loading"
            selectable
            :points="dataComputed?.points ? dataComputed.points : []"
            :target="dataComputed?.target"
            :quadrants="{
                topLeft: 'Higher Success • Lower Send',
                topRight: 'Higher Success • Higher Send',
                bottomLeft: 'Lower Success • Lower Send',
                bottomRight: 'Lower Success • Higher Send',
            }"
        >
            <template v-slot:title> {{ activeMetric.label }} </template>
            <template v-slot:subtitle> Groups are positioned relative to other groups across the team. </template>

            <template v-if="Boolean(dataComputed?.points)" v-slot:right>
                <vx-button color="muted" size="sm" @click="exportData" :loading="exporting" data-test="export-button">
                    Export <font-awesome-icon widthAuto :icon="faFileArrowDown" />
                </vx-button>
            </template>

            <template v-slot:x-axis-label>Send&nbsp;→</template>
            <template v-slot:y-axis-label>Success&nbsp;↑</template>

            <template v-slot:tooltip="{ point }">
                <div v-if="point.group_id" class="flex flex-row items-center gap-1.5 font-semibold">
                    <vx-badge color="slate" size="sm">{{ point.group_id }}</vx-badge> {{ point?.group_name || '' }}
                </div>

                <div>
                    <dl class="flex justify-between gap-1">
                        <dt class="flex justify-between font-extrabold text-slate-800">{{ activeMetric.label }}:</dt>
                        <dd>
                            {{ vxFormatPercentage(point.metric) }}
                        </dd>
                    </dl>

                    <dl class="flex justify-between gap-1">
                        <dt class="text-slate-500 font-normal">Audience Send:</dt>
                        <dd>
                            {{ vxFormatCount(point.messages) }}
                        </dd>
                    </dl>
                </div>

                <vx-button
                    :href="`/analytics/franchise-hub/${props.category}/${props.metric}/groups/${
                        point.group_id
                    }?${stringifyQueryString(fetchParams)}`"
                    outline
                    color="muted"
                    size="sm"
                    class="w-full"
                >
                    View <font-awesome-icon widthAuto :icon="faEye" />
                </vx-button>
            </template>

            <template v-if="Boolean(dataComputed?.points)" v-slot:footer>
                <div
                    class="w-full grow -mr-[45px] -mx-6 -mb-6 flex flex-col justify-between gap-2 rounded-b-2xl border-t border-t-slate-200 bg-slate-50 p-6 lg:flex-row"
                >
                    <vx-chart-dot-key
                        :keys="[
                            {
                                name: 'bad',
                                description: 'under performing median',
                                icon: faMeh,
                                class: 'border-rose-500 text-rose-600',
                            },
                            {
                                name: 'ok',
                                description: 'performing near median',
                                icon: faSmile,
                                class: 'border-violet-500 text-violet-600',
                            },
                            {
                                name: 'good',
                                description: 'over performing median',
                                icon: faFaceGrinStars,
                                class: 'border-sky-500 text-sky-600',
                            },
                        ]"
                    />
                </div>
                <vx-button color="transparent" size="xs" @click="explainModal = true">
                    <font-awesome-icon widthAuto :icon="faCircleQuestion" />
                </vx-button>
            </template>
        </vx-chart-dot>

        <div class="absolute inset-s-0 inset-bs-0 h-0 w-full">
            <transition
                mode="out-in"
                enter-active-class="transition duration-300"
                leave-active-class="transition duration-150"
                enter-from-class="opacity-0 scale-95 translate-y-8"
                enter-to-class="opacity-100 scale-100"
                leave-from-class="opacity-100 scale-100"
                leave-to-class="opacity-0 scale-95 translate-y-8"
            >
                <div
                    v-if="selectedDots.length"
                    class="fixed inset-be-4 inset-s-1/2 z-50 flex w-[calc(100vw-32px)] -translate-x-1/2 transform flex-row items-stretch gap-2 rounded-2xl border border-solid border-white bg-white/90 p-4 shadow-2xl shadow-sky-950/40 backdrop-blur-md transition sm:inset-be-16 sm:max-w-(--breakpoint-sm) sm:p-6 sm:pl-8"
                >
                    <div class="flex w-full items-center justify-between">
                        <span class="flex items-baseline gap-1 text-lg text-slate-700">
                            <span class="font-extrabold">{{ selectedDots.length }}</span>
                            groups selected
                        </span>

                        <div class="flex gap-2" id="floatingActions">
                            <vx-button color="muted-light" @click="clearSelection">Clear</vx-button>
                            <vx-button id="viewListButton" color="primary" @click="handleListClick()"
                                >View List <font-awesome-icon widthAuto :icon="faArrowRight"
                            /></vx-button>
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <vx-modal size="sm" v-model:visible="explainModal" :padding="false">
            <template v-slot:header>
                <div class="p-6 bg-white border-t-0 border-x-0 border-b border-solid border-b-slate-100">
                    {{ activeMetric.label }} Rate
                </div>
            </template>

            <div class="p-6 bg-slate-50 [&_p]:mb-6 lg:py-10 lg:px-12">
                <strong class="text-xl font-bold text-slate-700">{{ activeMetric.label }} Rate (y-axis)</strong>
                <p class="text-base">{{ activeMetric.description }}</p>

                <strong class="text-xl font-bold text-slate-700">Audience Sent (x-axis)</strong>
                <p class="text-base">
                    Its position in the axis is the quantity of contacts (Audience) messaged relative to the full amount
                    of message-able contacts available in the group
                </p>
            </div>
        </vx-modal>
    </div>
</template>
<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxBadge,
    VxButton,
    VxChartDot,
    VxChartDotKey,
    VxModal,
    vxFormatCount,
    vxFormatPercentage,
} from '@voxie/frontend-components';
import { ref, watch, computed } from 'vue';
import { outboundMessagesAnalyticsClient } from '~/services';
import { getMetricByValue, metricsInputValidator, metricsWithGoals } from '~/utils/analytics';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { analyticsCategoriesValues } from '~/constants/categories';
import {
    faArrowRight,
    faEye,
    faFaceGrinStars,
    faFileArrowDown,
    faMeh,
    faSmile,
} from '@fortawesome/pro-solid-svg-icons';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError, sendFormRequest } from '~/utils/request';
import { stringifyQueryString } from '~/utils/string';
import { download, prepareCsv } from '~/utils/files';

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    category: {
        type: String,
        required: true,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    metric: {
        type: String,
        required: true,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
});

const emit = defineEmits(['selected', 'deselected']);

const toasted = useToasted();
const explainModal = ref(false);

const { loading, response, fetchData } = useVersionedRequest();

const dataComputed = computed(() => {
    const currentMetricData = response?.value?.data;

    if (!currentMetricData?.points) {
        return {
            points: [],
        };
    }

    return {
        points: currentMetricData.points,
        target: {
            label: 'Goal',
            position: currentMetricData.target_line || 0,
            value: currentMetricData.target || 0,
        },
    };
});

const selectedDotsIndexes = ref([]);
const selectedDots = computed(() => {
    return dataComputed.value.points
        .filter((_, i) => selectedDotsIndexes.value.includes(String(i)))
        .map(({ group_id }) => group_id);
});
const context = computed(() => ({ teamId: Spark.state.currentTeam.id }));

const fetchMetrics = () => {
    fetchData(
        outboundMessagesAnalyticsClient.getCategoryMetricGroupPlot(
            context.value,
            props.category,
            props.metric,
            props.fetchParams
        )
    ).catch((e) => {
        console.error(e);
    });
};

watch(
    [() => props.fetchParams],
    () => {
        fetchMetrics();
    },
    { immediate: true, deep: true }
);

const activeMetric = computed(() => getMetricByValue(props.metric));

const handleSelection = (indexes) => {
    selectedDotsIndexes.value = indexes || [];

    if (selectedDotsIndexes.value.length) {
        emit('selected');
    } else {
        emit('deselected');
    }
};

const handleListClick = () => {
    if (!selectedDots.value) {
        return;
    }

    const url = new URL(`analytics/franchise-hub/${props.category}/${props.metric}/groups`, window.location.origin);
    url.search = stringifyQueryString(props.fetchParams);

    sendFormRequest(url.toString(), { group_ids: selectedDots.value });
};

const clearSelection = () => {
    selectedDotsIndexes.value = [];
    emit('deselected');
};

const exporting = ref(false);
const exportData = () => {
    if (exporting.value && (!dataComputed.value?.points?.length || !selectedDots.value.length)) {
        return;
    }

    exporting.value = true;

    const fields = [
        'group_id',
        'group_name',
        'unique_click_through_rate',
        'click_through_rate',
        'message_reply_rate',
        'opt_out_rate',
        'total_messages_sent',
    ];

    outboundMessagesAnalyticsClient
        .getExportGroupMetrics(context.value, props.fetchParams)
        .then((res) => {
            const metricGroups = selectedDots.value.length
                ? selectedDots.value
                : dataComputed.value.points.map(({ group_id }) => group_id);

            const list = (res?.data?.data || []).filter((groupMetrics) => {
                return metricGroups.includes(groupMetrics.group_id);
            });

            const rows = list.map((item) => fields.map((field) => item[field]));

            const csv = prepareCsv(fields, rows);

            download(csv, `${props.category}_group_metrics.csv`, { type: 'text/csv;charset=utf-8;' });

            exporting.value = false;
        })
        .catch((e) => {
            console.error(e);
            toasted.error(catchRequestError(e));
        })
        .finally(() => (exporting.value = false));
};

defineExpose({
    loading,
});
</script>
